import { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Theme } from '../types/enums';
import { ResolveIcon, useIconRegistry } from '../IconRegistryProvider';
import { useConfig } from '../Config/ConfigProvider';
import type { FAIconProps, IconTheme, IconName } from './types/propsType';
import { IconStyle, IconNames, Shade } from './types/enums';
import { mapAnimationToFAProps } from './iconsUtils';
import styles from './Icons.module.scss';
/** Fallback icon name resolved from the registry when the requested icon is not found. */
const FALLBACK_ICON = 'circle-question';

const FAIcon = forwardRef<HTMLSpanElement, FAIconProps>((props: FAIconProps, ref) => {
  const {
    theme = Theme.BLACK,
    iconStyle = IconStyle.REGULAR,
    shade = Shade.DARK,
    iconName,
    className,
    iconAnimationClassName = '',
    enableHover = true,
    ...rest
  } = props;

  const resolveIcon: ResolveIcon = useIconRegistry();
  const configs = useConfig();
  const isSVGEnabled = configs.NEXT_SVG_ICONS_ENABLED;

  if (!iconName) {
    return <></>;
  }
  // CSS class-based rendering
  if (!isSVGEnabled) {
    return (
      <span
        className={`fa-width-auto fa-${iconStyle} fa-${iconName} ${iconAnimationClassName} ${styles[theme]} ${
          styles[shade]
        } ${!enableHover ? styles['no-hover'] : ''} ${className || ''}`}
        ref={ref}
        {...rest}
      />
    );
  }

  const iconDef = resolveIcon(iconStyle, iconName);

  const finalIconDef = iconDef ?? resolveIcon(IconStyle.SOLID, FALLBACK_ICON);

  if (!finalIconDef) {
    return <></>;
  }

  const animationProps = mapAnimationToFAProps(iconAnimationClassName);

  return (
    <span
      className={`fa-width-auto ${styles['svg-icon']} ${styles[theme]} ${styles[shade]} ${
        !enableHover ? styles['no-hover'] : ''
      } ${className || ''}`.trim()}
      ref={ref}
      data-missing-icon={!iconDef ? iconName : undefined}
      {...rest}
    >
      <FontAwesomeIcon icon={finalIconDef} {...animationProps} style={{ fontSize: 'inherit' }} />
    </span>
  );
});

FAIcon.displayName = 'FAIcon';

export default FAIcon;
export { FAIcon, Shade, IconStyle, IconNames };
export type { FAIconProps, IconTheme, IconName };
